/**
 * HelpChartEmbed
 *
 * Renders an interactive chart from the registry with proper loading states.
 * Built on @shopify/polaris-viz for consistent styling.
 */

import { Suspense } from 'react';
import { Box, SkeletonDisplayText, Text, BlockStack } from '@shopify/polaris';
import { getChart, getChartMeta, type ChartProps } from './charts';

interface HelpChartEmbedProps {
  /** Name of the chart to render */
  name: string;
  /** Optional caption text */
  caption?: string;
  /** Override default height */
  height?: number;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Whether chart is interactive */
  interactive?: boolean;
  /** Additional CSS class */
  className?: string;
}

/** Loading skeleton for charts */
function ChartSkeleton({ height }: { height: number }) {
  return (
    <div
      className="help-chart-skeleton"
      style={{
        height: `${height}px`,
        width: '100%',
      }}
    >
      <SkeletonDisplayText size="medium" />
    </div>
  );
}

/** Error fallback when chart fails to load */
function ChartError({ name }: { name: string }) {
  return (
    <Box
      background="bg-surface-secondary"
      padding="400"
      borderRadius="200"
      borderColor="border"
      borderWidth="025"
    >
      <Text tone="subdued" as="p">
        Chart not available: {name}
      </Text>
    </Box>
  );
}

export function HelpChartEmbed({
  name,
  caption,
  height,
  showLegend = true,
  interactive = true,
  className,
}: HelpChartEmbedProps) {
  const Chart = getChart(name);
  const meta = getChartMeta(name);

  if (!Chart || !meta) {
    return <ChartError name={name} />;
  }

  const resolvedHeight = height ?? meta.defaultHeight;

  const chartProps: ChartProps = {
    className: `help-chart ${className ?? ''}`.trim(),
    height: resolvedHeight,
    showLegend,
    interactive: interactive && meta.interactive,
  };

  return (
    <figure
      className="help-chart-container"
      role="figure"
      aria-label={meta.title}
    >
      <BlockStack gap="200">
        <Box
          background="bg-surface"
          padding="400"
          borderRadius="200"
          borderColor="border"
          borderWidth="025"
        >
          <Suspense fallback={<ChartSkeleton height={resolvedHeight} />}>
            <div className="help-chart-wrapper">
              <Chart {...chartProps} />
            </div>
          </Suspense>
        </Box>
        {(caption || meta.title) && (
          <figcaption className="help-chart-caption">
            <Text tone="subdued" variant="bodySm" as="span">
              {caption ?? meta.title}
            </Text>
          </figcaption>
        )}
      </BlockStack>
    </figure>
  );
}

export default HelpChartEmbed;
